import React, { useState, useEffect } from "react";
import { Container, Nav, Navbar, Offcanvas, Row, Col } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import Marquee from "react-fast-marquee";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { FaHome, FaInfoCircle, FaBoxOpen, FaShieldAlt, FaPhoneAlt, FaFire } from 'react-icons/fa';

// --- Global Overrides for Scrollbar/Overflow ---
const GlobalStyle = createGlobalStyle`
  body, html {
    
    max-width: 100%;
  }
`;

const shimmer = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 100% 0; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
`;

const pulseBorder = keyframes`
  0% { border-color: rgba(255, 215, 0, 0.5); }
  50% { border-color: rgba(255, 215, 0, 1); }
  100% { border-color: rgba(255, 215, 0, 0.5); }
`;

// --- Styled Components ---

const TopBar = styled.div`
  background: linear-gradient(45deg, #FFD700, #FDB931, #FFD700, #FDB931);
  background-size: 400% 400%;
  animation: ${shimmer} 10s ease infinite;
  color: #3e2723;
  font-family: 'bold', sans-serif;
  padding: 8px 0;
  font-size: 0.95rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: relative;
  z-index: 1020; /* High z-index but below Offcanvas */
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 0;
  }
`;

const StyledNavbar = styled(Navbar)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: all 0.4s ease;
  padding: 10px 0;
  z-index: 1010;
  border-bottom: 3px solid transparent; 
  border-image: linear-gradient(to right, #b8860b, #ffecb3, #b8860b) 1;
  
  &.scrolled {
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    padding: 5px 0;
  }

  .navbar-toggler {
    border: none;
    box-shadow: none;
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }
`;

const LogoContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 105%; /* Reduced to prevent potential overflow */
    height: 105%;
    background: radial-gradient(circle, rgba(255,215,0,0.4) 0%, rgba(255,255,255,0) 70%);
    z-index: -1;
    animation: ${pulseBorder} 2s infinite;
  }
`;

const LogoImage = styled.img`
  width: 130px;
  height: 130px;
  object-fit: cover;
  border: 4px solid #fff;
  border-radius: 50%;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &:hover {
    transform: rotate(5deg) scale(1.05);
    box-shadow: 0 8px 25px rgba(255, 165, 0, 0.4);
  }

  @media (max-width: 991px) {
    width: 55px; /* Slightly smaller for mobile safety */
    height: 55px;
    border-width: 2px;
  }
`;

const NavLinkStyled = styled(Nav.Link)`
  color: #1a237e !important;
  font-family: 'bold', sans-serif;
  font-size: 16px;
  margin: 0 8px;
  padding: 10px 20px !important;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  transition: color 0.3s ease;
  text-decoration: none !important;

  
  svg {
    margin-right: 8px;
    transition: transform 0.3s ease;
  }

  &:hover {
    color: #b71c1c !important;
    background: rgba(255, 236, 179, 0.3);
    
    svg {
      transform: scale(1.2);
      color: #ff6f00;
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 50%;
    width: 0;
    height: 3px;
    background: linear-gradient(90deg, #ff6f00, #ffca28);
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  &:hover::after, &.active::after {
    width: 80%;
  }
`;

const OrderButton = styled.button`
  background: linear-gradient(135deg, #d50000 0%, #ff5722 100%);
  color: white;
  font-family: 'bold', sans-serif;
  border: none;
  border-radius: 50px;
  padding: 12px 30px;
  font-size: 1rem;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(213, 0, 0, 0.4);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    transition: 0.5s;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(213, 0, 0, 0.6);
    
    &::before {
      left: 100%;
    }
    
    svg {
      animation: ${float} 1s ease infinite;
    }
  }
`;

const MobileOffcanvas = styled(Offcanvas)`
  background: linear-gradient(180deg, #fff9c4 0%, #ffffff 100%);
  z-index: 1050; /* Ensure it's on top */
  
  .offcanvas-header {
    border-bottom: 2px solid #ffd54f;
  }
  .offcanvas-title {
    font-family: 'bold', sans-serif;
    color: #e65100;
  }
`;

const AppBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => setShowMobileMenu((prev) => !prev);
  const closeMobileMenu = () => setShowMobileMenu(false);

  const navLinks = [
    { to: "/home", icon: FaHome, text: "Home" },
    { to: "/about", icon: FaInfoCircle, text: "About" },
    { to: "/", icon: FaBoxOpen, text: "Products" },
    { to: "/safetytips", icon: FaShieldAlt, text: "Safety Tips" },
    { to: "/contact", icon: FaPhoneAlt, text: "Contact" },
  ];

  return (
    <>
      <GlobalStyle />
      <TopBar>
        <Container fluid>
          <Marquee gradient={false} speed={40} pauseOnHover={true}>
            <span className="mx-4">🎉✨ <span className="fw-bold"> தொழிற்சாலை நேரடி விற்பனை </span> 🏭</span>
            <span className="mx-4">| 🛍️ எங்களிடம் <span className="fw-bold">மொத்தம்மாகவும் & சில்லரையாகவும் பட்டாசுகள் </span> கிடைக்கும்</span>
            <span className="mx-4">| 📦 அனைவருக்கும் 2026 ஆம் ஆண்டு அட்வான்ஸ் Happy Diwali 🛒</span>
            <span className="mx-4">| 📲 Whatsapp : <span className="fw-bold">+91 9443155605</span></span>
            <span className="mx-4">| 📞 Call : <span className="fw-bold">+91 9843660591</span></span>
            <span className="mx-4">| 📧 Email : <span className="fw-bold">Jaivanthcrackers@gmail.com</span></span>
          </Marquee>
        </Container>
      </TopBar>

      <StyledNavbar 
        expand="lg" 
        expanded={false} // Force unexpanded state for standard Navbar collapse, since we use custom Offcanvas
        className={`${scrolled ? 'scrolled' : ''}`}
      >
        <Container>
          <Navbar.Brand as={NavLink} to="/" className="p-0 d-flex align-items-center">
            <LogoContainer>
              <LogoImage
                src={require("../../assets/images/storelogo.png")}
                alt="Jaivanth Crackers"
              />
            </LogoContainer>
          </Navbar.Brand>

          <Navbar.Toggle 
            aria-controls="offcanvas-navbar" 
            onClick={toggleMobileMenu}
            style={{ border: 'none', boxShadow: 'none' }}
          >
            <div style={{ padding: '8px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ffd54f' }}>
              <span className="navbar-toggler-icon" />
            </div>
          </Navbar.Toggle>

          {/* Desktop Menu */}
          <Navbar.Collapse className="d-none d-lg-block">
            <Nav className="ms-auto align-items-center">
              {navLinks.map((link, index) => (
                <NavLinkStyled 
                  key={index} 
                  as={NavLink} 
                  to={link.to} 
                  exact
                >
                  <link.icon className="mb-1" /> {link.text}
                </NavLinkStyled>
              ))}
              <Nav.Link as={NavLink} to="/" className="ms-3">
                <OrderButton>
                  <FaFire /> Order Now
                </OrderButton>
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>

          {/* Mobile Menu (Offcanvas) */}
          <MobileOffcanvas 
            show={showMobileMenu} 
            onHide={closeMobileMenu} 
            placement="end"
            id="offcanvas-navbar"
            scroll={true} // Allow background scroll to prevent overflow sticking
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>🎆 Menu</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <Nav className="flex-column text-center">
                 {navLinks.map((link, index) => (
                  <NavLinkStyled 
                    key={index} 
                    as={NavLink} 
                    to={link.to} 
                    onClick={closeMobileMenu}
                    className="my-3 py-2"
                  >
                    <link.icon /> {link.text}
                  </NavLinkStyled>
                ))}
                <div className="mt-4 d-flex justify-content-center">
                   <LinkWrapper onClick={closeMobileMenu}>
                       <OrderButton>
                        <FaFire /> Order Now
                      </OrderButton>
                   </LinkWrapper>
                </div>
              </Nav>
            </Offcanvas.Body>
          </MobileOffcanvas>

        </Container>
      </StyledNavbar>
    </>
  );
};

// Wrapper specifically for the order button link in mobile
const LinkWrapper = styled.div`
  display: inline-block;
`;

export default AppBar;
